import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LogService } from './log.service';
import { MessagesService } from './messages.service';
import { CommonService } from './common.service';
import { CacheService } from './cache.service';
import { Observable } from 'rxjs/Observable';
import { tap, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { PageOption } from '../models/common/pageOption';
import { DailyTripFilter } from '../models/trips/dailyTripFilter';
import { DailyTrip } from '../models/trips/dailyTrip';
import { RequestResult } from '../models/common/requestResult';
import { PageOptionResult } from '../models/common/pageOptionResult';
import { ErrorCollection } from '../models/common/errorCollection';
import { DailyTripHistoryFilter } from '../models/trips/dailyTripHistoryFilter';
import { DailyTripHistory } from '../models/trips/dailyTripHistory';
import { IDailyTripTicket } from '../models/trips/dailyTripTicket';
import { Result } from '../models/common/result';
import { DailyTripType } from '../models/trips/dailyTripType';
import { CACHE_KEYS } from '../contants/CACHE_KEYS';
import { of } from 'rxjs/observable/of';

@Injectable()
export class DailyTripService {
	private origin: string = environment.API_BASE_URL;

	constructor(
		private _http: HttpClient,
		private _logService: LogService,
		private _messagesService: MessagesService,
		private _common: CommonService,
		private _cacheService: CacheService
	) { }

	getDailyTrip(id: string): Observable<DailyTrip> {
		return this._http.get<DailyTrip>(`${this.origin}/api/dailyTrips/${id}`)
			.pipe(
				tap(x => this.log(`getDailyTrip: id=${id}`)),
				catchError(this._logService.handleError<DailyTrip>('getDailyTrip'))
			);
	}

	getDailyTripTricket(id: string): Observable<Result<IDailyTripTicket>> {
		return this._http.get<Result<IDailyTripTicket>>(`${this.origin}/api/dailyTrips/${id}/trip-ticket`)
			.pipe(
				tap(x => this.log(`getDailyTripTricket: id=${id}`)),
				catchError(this._logService.handleError<Result<IDailyTripTicket>>('getDailyTripTricket'))
			);
	}

	getDailyTripsList(filter: PageOption<DailyTripFilter>): Observable<PageOptionResult<DailyTrip>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PageOptionResult<DailyTrip>>(`${this.origin}/api/dailyTrips${param}`)
			.pipe(
				tap(x => this.log(`getDailyTripsList param = ${param}`)),
				catchError(this._logService.handleError<PageOptionResult<DailyTrip>>('getDailyTripsList'))
			);
	}

	getDailyTripHistoryList(filter: PageOption<DailyTripHistoryFilter>): Observable<PageOptionResult<DailyTripHistory>> {
		let param = this._common.objectToQueryParam(filter);
		return this._http.get<PageOptionResult<DailyTripHistory>>(`${this.origin}/api/dailyTrips/history${param}`)
			.pipe(
				tap(x => this.log(`getDailyTripHistoryList param = ${param}`)),
				catchError(this._logService.handleError<PageOptionResult<DailyTripHistory>>('getDailyTripHistoryList'))
			);
	}

	addDailyTrip(dailyTrip: DailyTrip): Observable<RequestResult<DailyTrip>> {
		return this._http.post<RequestResult<DailyTrip>>(`${this.origin}/api/dailyTrips`, JSON.stringify(dailyTrip))
			.pipe(
				tap(x => this.log(`addDailyTrip: id=${dailyTrip.id}`)),
				catchError(this._logService.handleError<RequestResult<DailyTrip>>('addDailyTrip'))
			);
	}

	updateDailyTrip(dailyTrip: DailyTrip): Observable<RequestResult<DailyTrip>> {
		return this._http.put<RequestResult<DailyTrip>>(`${this.origin}/api/dailyTrips/${dailyTrip.id}`, JSON.stringify(dailyTrip))
			.pipe(
				tap(x => this.log(`updateDailyTrip: id=${dailyTrip.id}`)),
				catchError(this._logService.handleError<RequestResult<DailyTrip>>('updateDailyTrip'))
			);
	}

	deleteDailyTrip(id: number): Observable<ErrorCollection> {
		return this._http.delete<ErrorCollection>(`${this.origin}/api/dailyTrips/${id}`)
			.pipe(
				tap(x => this.log(`deleteDailyTrip: id=${id}`)),
				catchError(this._logService.handleError<ErrorCollection>('deleteDailyTrip'))
			);
    }
    
    exportDailyTrip(tripDateFrom: string, tripDateTo: string) {
        return this._http.get(`${this.origin}/api/dailyTrips/export?tripDateFrom=${tripDateFrom}&tripDateTo=${tripDateTo}`, {responseType: 'arraybuffer'});
    } 

	getDailyTripTypes(): Observable<DailyTripType[]> {
		var cacheKey = CACHE_KEYS.dailyTripTypes;
		var url = `${this.origin}/api/daily-trip-types`;

		let value = this._cacheService.get(cacheKey)
		if (value != null) {
			return of(value);
		}

		return this._http.get<DailyTripType[]>(url)
			.pipe(
				tap(x => {
					this.log(`getDailyTripTypes`);
					this._cacheService.add(cacheKey, x, 10 * 60 * 1000);
				}),
				catchError(this._logService.handleError<DailyTripType[]>('getDailyTripTypes'))
			);
	}

	copy(source: DailyTrip): DailyTrip {
		if(source === undefined || source === null){
			return null;
		}
		let copy = new DailyTrip();
		copy.id = source.id;
		copy.tripDate = source.tripDate;
		copy.vehicleId = source.vehicleId;
		copy.driverId = source.driverId;
		copy.helperId = source.helperId;
		copy.bookKeeperId = source.bookKeeperId;
		copy.tripId = source.tripId;
		copy.tripContainerDetailId = source.tripContainerDetailId;
		copy.returnDestination = source.returnDestination;
		copy.createdBy = source.createdBy;
		copy.createdDate = source.createdDate;
		copy.updatedBy = source.updatedBy;
		copy.updatedDate = source.updatedDate;
		return copy;
	}

	private log(message: string) {
		this._messagesService.add('DailyTripService: ' + message);
	}

}



// WEBPACK FOOTER //
// ./src/app/services/daily-trip.service.ts